import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Plus,
  RefreshCw,
  Save,
  Search,
  Shield,
  Trash2,
  Users,
} from 'lucide-react';

import { useAuth } from '@/Modules/Core/Context/AuthContext';
import { AppMetricCard, AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { EmptyState } from '@/Modules/Core/Components/Layout/EmptyState';
import { Badge } from '@/Modules/Core/Components/shadcn/badge';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Modules/Core/Components/shadcn/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/Modules/Core/Components/shadcn/dialog';
import { Input } from '@/Modules/Core/Components/shadcn/input';
import { Label } from '@/Modules/Core/Components/shadcn/label';
import { MultiSelect } from '@/Modules/Core/Components/shadcn/multi-select';
import { Textarea } from '@/Modules/Core/Components/shadcn/textarea';
import { cn } from '@/lib/utils';
import {
  createPermission,
  createRole,
  deletePermission,
  deleteRole,
  getAuthorizationUser,
  getPermission,
  getRole,
  listAuthorizationUsers,
  listPermissions,
  listRoles,
  updateAuthorizationUserPermissions,
  updateAuthorizationUserRoles,
  updatePermission,
  updateRole,
  updateRolePermissions,
} from '../Services/AuthorizationService';

const TAB_OPTIONS = [
  {
    id: 'users',
    label: 'User Access',
    description: 'Assign roles and direct permissions to workspace users.',
    icon: Users,
  },
  {
    id: 'roles',
    label: 'Roles',
    description: 'Create, update, and retire permission bundles.',
    icon: Shield,
  },
  {
    id: 'permissions',
    label: 'Permissions',
    description: 'Manage the underlying permission catalog.',
    icon: KeyRound,
  },
];

const NEW_ROLE_KEY = '__new-role__';
const NEW_PERMISSION_KEY = '__new-permission__';

const getRoleId = (role) => role?.role_id || role?.id || '';
const getPermissionId = (permission) =>
  permission?.permission_id || permission?.id || '';
const getUserId = (item) => item?.user_id || item?.id || '';
const getPermissionLabel = (permission) =>
  permission?.display_name || permission?.displayName || permission?.name || 'Unnamed permission';

const createInitialRoleForm = () => ({
  name: '',
  description: '',
  active: true,
});

const createInitialPermissionForm = () => ({
  name: '',
  display_name: '',
  module: '',
  description: '',
  active: true,
});

const extractRolePermissionIds = (roleDetail) => {
  const permissionSources = [
    roleDetail?.permissions,
    roleDetail?.assigned_permissions,
    roleDetail?.permission_assignments,
  ];

  for (const source of permissionSources) {
    if (Array.isArray(source)) {
      return source.map(getPermissionId).filter(Boolean);
    }
  }

  return [];
};

const extractUserRoleIds = (userDetail) =>
  (Array.isArray(userDetail?.roles) ? userDetail.roles : [])
    .map(getRoleId)
    .filter(Boolean);

const extractUserDirectPermissionIds = (userDetail) => {
  if (Array.isArray(userDetail?.direct_permissions)) {
    return userDetail.direct_permissions
      .map(getPermissionId)
      .filter(Boolean);
  }

  return (Array.isArray(userDetail?.permissions) ? userDetail.permissions : [])
    .filter((permission) => permission?.direct_assigned)
    .map(getPermissionId)
    .filter(Boolean);
};

const extractPermissionAssignments = (permissionDetail) => {
  const assignedRoles =
    permissionDetail?.roles ||
    permissionDetail?.assigned_roles ||
    permissionDetail?.via_roles ||
    [];
  const assignedUsers =
    permissionDetail?.users ||
    permissionDetail?.direct_users ||
    permissionDetail?.assigned_users ||
    [];

  return {
    assignedRoles: Array.isArray(assignedRoles) ? assignedRoles : [],
    assignedUsers: Array.isArray(assignedUsers) ? assignedUsers : [],
  };
};

const TabButton = ({ active, icon: Icon, label, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex w-full cursor-pointer items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-colors',
      active
        ? 'border-primary/35 bg-primary/8 text-foreground'
        : 'border-border/45 bg-background/70 text-foreground hover:border-border/65 hover:bg-card/50'
    )}
  >
    <span
      className={cn(
        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
        active ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
      )}
    >
      <Icon className="h-4 w-4" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-semibold">{label}</span>
      <span className="mt-1 block text-[12px] leading-5 text-muted-foreground">
        {description}
      </span>
    </span>
  </button>
);

const SearchField = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    <Input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="pl-9"
    />
  </div>
);

const AssignmentBadgeList = ({ items = [], getKey, getLabel, emptyLabel }) => {
  if (!items.length) {
    return (
      <p className="text-[12px] leading-5 text-muted-foreground">{emptyLabel}</p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, index) => (
        <Badge
          key={getKey(item, index)}
          variant="outline"
          className="rounded-full px-3 py-1 text-[11px]"
        >
          {getLabel(item)}
        </Badge>
      ))}
    </div>
  );
};

const SidebarList = ({
  title,
  description,
  countLabel,
  searchValue,
  onSearchChange,
  actions,
  loading,
  items,
  selectedId,
  onSelect,
  getItemId,
  renderItem,
  emptyTitle,
  emptyDescription,
}) => (
  <Card className="overflow-hidden">
    <CardHeader className="border-b border-border/50">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle className="text-[16px]">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {actions}
      </div>
      <div className="space-y-3 pt-3">
        <SearchField
          value={searchValue}
          onChange={onSearchChange}
          placeholder={`Search ${title.toLowerCase()}...`}
        />
        <div className="text-[12px] text-muted-foreground">{countLabel}</div>
      </div>
    </CardHeader>
    <CardContent className="px-3 py-3">
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={`${title}-loading-${index + 1}`}
              className="h-[72px] animate-pulse rounded-2xl bg-muted/55"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Search}
          title={emptyTitle}
          description={emptyDescription}
          className="py-10"
        />
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const itemId = getItemId(item);
            const active = itemId === selectedId;

            return (
              <button
                key={itemId}
                type="button"
                onClick={() => onSelect(itemId)}
                className={cn(
                  'flex w-full cursor-pointer flex-col items-start gap-2 rounded-2xl border px-4 py-3 text-left transition-colors',
                  active
                    ? 'border-primary/30 bg-primary/10'
                    : 'border-border/50 bg-background/70 hover:border-border/70 hover:bg-card/40'
                )}
              >
                {renderItem(item, active)}
              </button>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

const Authorization = () => {
  const queryClient = useQueryClient();
  const { hydrateCurrentUser, user } = useAuth();

  const [activeTab, setActiveTab] = React.useState('users');
  const [feedbackDialog, setFeedbackDialog] = React.useState({
    open: false,
    tone: 'success',
    title: '',
    message: '',
  });
  const [confirmRoleDeleteOpen, setConfirmRoleDeleteOpen] = React.useState(false);
  const [confirmPermissionDeleteOpen, setConfirmPermissionDeleteOpen] =
    React.useState(false);

  const [userSearch, setUserSearch] = React.useState('');
  const [selectedUserId, setSelectedUserId] = React.useState(null);
  const deferredUserSearch = React.useDeferredValue(userSearch);
  const [selectedUserRoleIds, setSelectedUserRoleIds] = React.useState([]);
  const [selectedUserPermissionIds, setSelectedUserPermissionIds] = React.useState([]);

  const [roleSearch, setRoleSearch] = React.useState('');
  const [includeInactiveRoles, setIncludeInactiveRoles] = React.useState(false);
  const [selectedRoleId, setSelectedRoleId] = React.useState(null);
  const deferredRoleSearch = React.useDeferredValue(roleSearch);
  const [roleForm, setRoleForm] = React.useState(createInitialRoleForm);
  const [selectedRolePermissionIds, setSelectedRolePermissionIds] = React.useState([]);

  const [permissionSearch, setPermissionSearch] = React.useState('');
  const [permissionModuleFilter, setPermissionModuleFilter] = React.useState('');
  const [includeInactivePermissions, setIncludeInactivePermissions] =
    React.useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = React.useState(null);
  const deferredPermissionSearch = React.useDeferredValue(permissionSearch);
  const deferredPermissionModuleFilter =
    React.useDeferredValue(permissionModuleFilter);
  const [permissionForm, setPermissionForm] = React.useState(
    createInitialPermissionForm
  );

  const refreshCurrentUserIfNeeded = React.useCallback(async () => {
    if (user?.user_id) {
      await hydrateCurrentUser();
    }
  }, [hydrateCurrentUser, user?.user_id]);

  const closeFeedbackDialog = React.useCallback(() => {
    setFeedbackDialog((current) => ({
      ...current,
      open: false,
    }));
  }, []);

  const handleMutationSuccess = React.useCallback(
    async (message, queryKeys = []) => {
      await Promise.all(
        queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      );
      await refreshCurrentUserIfNeeded();
      setFeedbackDialog({
        open: true,
        tone: 'success',
        title: 'Success',
        message,
      });
    },
    [queryClient, refreshCurrentUserIfNeeded]
  );

  const handleMutationError = React.useCallback((error, fallbackMessage) => {
    setFeedbackDialog({
      open: true,
      tone: 'error',
      title: 'Something went wrong',
      message: error?.message || fallbackMessage,
    });
  }, []);

  const usersQuery = useQuery({
    queryKey: ['authorization-users', deferredUserSearch],
    queryFn: () =>
      listAuthorizationUsers({
        search: deferredUserSearch,
        pageSize: 25,
        pageNumber: 1,
      }),
    staleTime: 30000,
  });

  const rolesQuery = useQuery({
    queryKey: ['authorization-roles', deferredRoleSearch, includeInactiveRoles],
    queryFn: () =>
      listRoles({
        search: deferredRoleSearch,
        includeInactive: includeInactiveRoles,
      }),
    staleTime: 30000,
  });

  const permissionsQuery = useQuery({
    queryKey: [
      'authorization-permissions',
      deferredPermissionSearch,
      deferredPermissionModuleFilter,
      includeInactivePermissions,
    ],
    queryFn: () =>
      listPermissions({
        search: deferredPermissionSearch,
        module: deferredPermissionModuleFilter,
        includeInactive: includeInactivePermissions,
      }),
    staleTime: 30000,
  });

  const roleOptionsQuery = useQuery({
    queryKey: ['authorization-role-options'],
    queryFn: () =>
      listRoles({
        includeInactive: true,
      }),
    staleTime: 30000,
  });

  const permissionOptionsQuery = useQuery({
    queryKey: ['authorization-permission-options'],
    queryFn: () =>
      listPermissions({
        includeInactive: true,
      }),
    staleTime: 30000,
  });

  const userDetailQuery = useQuery({
    queryKey: ['authorization-user-detail', selectedUserId],
    queryFn: () => getAuthorizationUser(selectedUserId),
    enabled: Boolean(selectedUserId),
  });

  const roleDetailQuery = useQuery({
    queryKey: ['authorization-role-detail', selectedRoleId],
    queryFn: () => getRole(selectedRoleId),
    enabled: Boolean(selectedRoleId) && selectedRoleId !== NEW_ROLE_KEY,
  });

  const permissionDetailQuery = useQuery({
    queryKey: ['authorization-permission-detail', selectedPermissionId],
    queryFn: () => getPermission(selectedPermissionId),
    enabled:
      Boolean(selectedPermissionId) && selectedPermissionId !== NEW_PERMISSION_KEY,
  });

  const saveUserRolesMutation = useMutation({
    mutationFn: ({ userId, roleIds }) => updateAuthorizationUserRoles(userId, roleIds),
    onSuccess: async () => {
      await handleMutationSuccess('User roles saved successfully.', [
        ['authorization-user-detail', selectedUserId],
        ['authorization-users'],
      ]);
    },
    onError: (error) =>
      handleMutationError(error, 'Unable to save user roles.'),
  });

  const saveUserPermissionsMutation = useMutation({
    mutationFn: ({ userId, permissionIds }) =>
      updateAuthorizationUserPermissions(userId, permissionIds),
    onSuccess: async () => {
      await handleMutationSuccess('User direct permissions saved successfully.', [
        ['authorization-user-detail', selectedUserId],
        ['authorization-users'],
      ]);
    },
    onError: (error) =>
      handleMutationError(error, 'Unable to save direct permissions.'),
  });

  const saveRoleMutation = useMutation({
    mutationFn: (payload) => {
      if (selectedRoleId && selectedRoleId !== NEW_ROLE_KEY) {
        return updateRole(selectedRoleId, payload);
      }

      return createRole(payload);
    },
    onSuccess: async (result) => {
      const nextRoleId = getRoleId(result) || selectedRoleId;
      await handleMutationSuccess('Role details saved successfully.', [
        ['authorization-roles'],
        ['authorization-role-options'],
        ['authorization-role-detail', selectedRoleId],
      ]);

      if (nextRoleId) {
        setSelectedRoleId(nextRoleId);
      }
    },
    onError: (error) => handleMutationError(error, 'Unable to save role.'),
  });

  const saveRolePermissionsMutation = useMutation({
    mutationFn: ({ roleId, permissionIds }) =>
      updateRolePermissions(roleId, permissionIds),
    onSuccess: async () => {
      await handleMutationSuccess('Role permissions saved successfully.', [
        ['authorization-role-detail', selectedRoleId],
        ['authorization-roles'],
      ]);
    },
    onError: (error) =>
      handleMutationError(error, 'Unable to save role permissions.'),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: () => deleteRole(selectedRoleId),
    onSuccess: async () => {
      const currentRoles = rolesQuery.data?.roles ?? [];
      const nextRole = currentRoles.find(
        (role) => getRoleId(role) && getRoleId(role) !== selectedRoleId
      );

      await handleMutationSuccess('Role deleted successfully.', [
        ['authorization-roles'],
        ['authorization-role-options'],
      ]);
      setConfirmRoleDeleteOpen(false);
      setSelectedRoleId(nextRole ? getRoleId(nextRole) : null);
    },
    onError: (error) => handleMutationError(error, 'Unable to delete role.'),
  });

  const savePermissionMutation = useMutation({
    mutationFn: (payload) => {
      if (selectedPermissionId && selectedPermissionId !== NEW_PERMISSION_KEY) {
        return updatePermission(selectedPermissionId, payload);
      }

      return createPermission(payload);
    },
    onSuccess: async (result) => {
      const nextPermissionId = getPermissionId(result) || selectedPermissionId;
      await handleMutationSuccess('Permission details saved successfully.', [
        ['authorization-permissions'],
        ['authorization-permission-options'],
        ['authorization-permission-detail', selectedPermissionId],
      ]);

      if (nextPermissionId) {
        setSelectedPermissionId(nextPermissionId);
      }
    },
    onError: (error) =>
      handleMutationError(error, 'Unable to save permission.'),
  });

  const deletePermissionMutation = useMutation({
    mutationFn: () => deletePermission(selectedPermissionId),
    onSuccess: async () => {
      const currentPermissions = permissionsQuery.data?.permissions ?? [];
      const nextPermission = currentPermissions.find(
        (permission) =>
          getPermissionId(permission) &&
          getPermissionId(permission) !== selectedPermissionId
      );

      await handleMutationSuccess('Permission deleted successfully.', [
        ['authorization-permissions'],
        ['authorization-permission-options'],
      ]);
      setConfirmPermissionDeleteOpen(false);
      setSelectedPermissionId(
        nextPermission ? getPermissionId(nextPermission) : null
      );
    },
    onError: (error) =>
      handleMutationError(error, 'Unable to delete permission.'),
  });

  React.useEffect(() => {
    const users = usersQuery.data?.users ?? [];

    if (!users.length) {
      setSelectedUserId(null);
      return;
    }

    const hasSelectedUser = users.some((item) => getUserId(item) === selectedUserId);
    if (!hasSelectedUser) {
      setSelectedUserId(getUserId(users[0]));
    }
  }, [selectedUserId, usersQuery.data?.users]);

  React.useEffect(() => {
    const roles = rolesQuery.data?.roles ?? [];

    if (selectedRoleId === NEW_ROLE_KEY) {
      return;
    }

    if (!roles.length) {
      setSelectedRoleId(null);
      return;
    }

    const hasSelectedRole = roles.some((item) => getRoleId(item) === selectedRoleId);
    if (!hasSelectedRole) {
      setSelectedRoleId(getRoleId(roles[0]));
    }
  }, [rolesQuery.data?.roles, selectedRoleId]);

  React.useEffect(() => {
    const permissions = permissionsQuery.data?.permissions ?? [];

    if (selectedPermissionId === NEW_PERMISSION_KEY) {
      return;
    }

    if (!permissions.length) {
      setSelectedPermissionId(null);
      return;
    }

    const hasSelectedPermission = permissions.some(
      (item) => getPermissionId(item) === selectedPermissionId
    );
    if (!hasSelectedPermission) {
      setSelectedPermissionId(getPermissionId(permissions[0]));
    }
  }, [permissionsQuery.data?.permissions, selectedPermissionId]);

  React.useEffect(() => {
    const userDetail = userDetailQuery.data;

    if (!userDetail) {
      setSelectedUserRoleIds([]);
      setSelectedUserPermissionIds([]);
      return;
    }

    setSelectedUserRoleIds(extractUserRoleIds(userDetail));
    setSelectedUserPermissionIds(extractUserDirectPermissionIds(userDetail));
  }, [userDetailQuery.data]);

  React.useEffect(() => {
    if (selectedRoleId === NEW_ROLE_KEY) {
      setRoleForm(createInitialRoleForm());
      setSelectedRolePermissionIds([]);
      return;
    }

    const roleDetail = roleDetailQuery.data;
    if (!roleDetail) {
      return;
    }

    setRoleForm({
      name: roleDetail?.name || '',
      description: roleDetail?.description || '',
      active: roleDetail?.active !== false,
    });
    setSelectedRolePermissionIds(extractRolePermissionIds(roleDetail));
  }, [roleDetailQuery.data, selectedRoleId]);

  React.useEffect(() => {
    if (selectedPermissionId === NEW_PERMISSION_KEY) {
      setPermissionForm(createInitialPermissionForm());
      return;
    }

    const permissionDetail = permissionDetailQuery.data;
    if (!permissionDetail) {
      return;
    }

    setPermissionForm({
      name: permissionDetail?.name || '',
      display_name:
        permissionDetail?.display_name || permissionDetail?.displayName || '',
      module: permissionDetail?.module || '',
      description: permissionDetail?.description || '',
      active: permissionDetail?.active !== false,
    });
  }, [permissionDetailQuery.data, selectedPermissionId]);

  const roleOptions = React.useMemo(
    () =>
      (roleOptionsQuery.data?.roles ?? []).map((role) => ({
        value: getRoleId(role),
        label: role?.name || 'Unnamed role',
      })),
    [roleOptionsQuery.data?.roles]
  );

  const permissionOptions = React.useMemo(
    () =>
      (permissionOptionsQuery.data?.permissions ?? []).map((permission) => ({
        value: getPermissionId(permission),
        label: getPermissionLabel(permission),
      })),
    [permissionOptionsQuery.data?.permissions]
  );

  const availableModules = React.useMemo(() => {
    const moduleNames = new Set();

    (permissionOptionsQuery.data?.permissions ?? []).forEach((permission) => {
      if (permission?.module) {
        moduleNames.add(permission.module);
      }
    });

    return Array.from(moduleNames).sort((left, right) => left.localeCompare(right));
  }, [permissionOptionsQuery.data?.permissions]);

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([
      usersQuery.refetch(),
      rolesQuery.refetch(),
      permissionsQuery.refetch(),
      roleOptionsQuery.refetch(),
      permissionOptionsQuery.refetch(),
      selectedUserId ? userDetailQuery.refetch() : Promise.resolve(),
      selectedRoleId && selectedRoleId !== NEW_ROLE_KEY
        ? roleDetailQuery.refetch()
        : Promise.resolve(),
      selectedPermissionId && selectedPermissionId !== NEW_PERMISSION_KEY
        ? permissionDetailQuery.refetch()
        : Promise.resolve(),
    ]);
  }, [
    permissionDetailQuery,
    permissionOptionsQuery,
    permissionsQuery,
    roleDetailQuery,
    roleOptionsQuery,
    rolesQuery,
    selectedPermissionId,
    selectedRoleId,
    selectedUserId,
    userDetailQuery,
    usersQuery,
  ]);

  const activeUserDetail = userDetailQuery.data;
  const activeRoleDetail =
    selectedRoleId === NEW_ROLE_KEY ? null : roleDetailQuery.data ?? null;
  const activePermissionDetail =
    selectedPermissionId === NEW_PERMISSION_KEY
      ? null
      : permissionDetailQuery.data ?? null;
  const permissionAssignments = React.useMemo(
    () => extractPermissionAssignments(activePermissionDetail),
    [activePermissionDetail]
  );

  return (
    <div className="tw">
      <div className="space-y-6">
        <AppPageSection>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <AppMetricCard
              label="Users"
              value={usersQuery.data?.totalCount ?? '--'}
              description="Workspace users available for role and permission assignment."
              icon={Users}
            />
            <AppMetricCard
              label="Roles"
              value={rolesQuery.data?.totalCount ?? '--'}
              description="Reusable permission bundles applied across the rollout app."
              icon={Shield}
            />
            <AppMetricCard
              label="Permissions"
              value={permissionsQuery.data?.totalCount ?? '--'}
              description="Permission catalog exposed by the backend authorization APIs."
              icon={KeyRound}
            />
          </div>

          <Card>
            <CardHeader className="border-b border-border/50">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle className="text-[20px]">Authorization Management</CardTitle>
                  <CardDescription className="mt-1 max-w-3xl">
                    Manage user assignments, curate reusable roles, and maintain the
                    permission catalog that drives rollout access across the app.
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={
                    usersQuery.isFetching ||
                    rolesQuery.isFetching ||
                    permissionsQuery.isFetching
                  }
                >
                  <RefreshCw
                    className={cn(
                      'mr-2 h-4 w-4',
                      (usersQuery.isFetching ||
                        rolesQuery.isFetching ||
                        permissionsQuery.isFetching) &&
                        'animate-spin'
                    )}
                  />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-3 px-5 py-5 md:grid-cols-3">
              {TAB_OPTIONS.map((tab) => (
                <TabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  description={tab.description}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </CardContent>
          </Card>

          {activeTab === 'users' ? (
            <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
              <SidebarList
                title="Users"
                description="Select a user to review effective access and assignment state."
                countLabel={`${usersQuery.data?.totalCount ?? 0} users`}
                searchValue={userSearch}
                onSearchChange={setUserSearch}
                loading={usersQuery.isLoading}
                items={usersQuery.data?.users ?? []}
                selectedId={selectedUserId}
                onSelect={setSelectedUserId}
                getItemId={getUserId}
                renderItem={(item) => (
                  <>
                    <div className="flex w-full items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-[14px] font-semibold text-foreground">
                          {item?.username || item?.name || 'Unknown user'}
                        </div>
                        <div className="truncate text-[12px] text-muted-foreground">
                          {item?.email_address || item?.email || 'No email provided'}
                        </div>
                      </div>
                      <Badge
                        variant={item?.active === false || item?.active === 0 ? 'outline' : 'secondary'}
                        className="rounded-full px-2.5 py-1"
                      >
                        {item?.active === false || item?.active === 0 ? 'Inactive' : 'Active'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      {item?.role_names?.length ? (
                        <span>{`${item.role_names.length} roles`}</span>
                      ) : null}
                      {item?.permission_names?.length ? (
                        <span>{`${item.permission_names.length} permissions`}</span>
                      ) : null}
                    </div>
                  </>
                )}
                emptyTitle="No users found"
                emptyDescription="Try a different search term or adjust the backend user catalog."
              />

              <Card>
                <CardHeader className="border-b border-border/50">
                  <CardTitle className="text-[18px]">User Assignments</CardTitle>
                  <CardDescription>
                    Replace role assignments and direct permissions for the selected user.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 px-5 py-5">
                  {userDetailQuery.isLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={`user-detail-loading-${index + 1}`}
                          className="h-20 animate-pulse rounded-2xl bg-muted/55"
                        />
                      ))}
                    </div>
                  ) : !activeUserDetail ? (
                    <EmptyState
                      icon={Users}
                      title="Select a user"
                      description="Choose a user from the left to manage role assignments and direct permissions."
                    />
                  ) : (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="rounded-2xl border border-border/50 bg-background/60 p-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Username
                          </p>
                          <p className="mt-2 text-[14px] font-semibold text-foreground">
                            {activeUserDetail?.username || activeUserDetail?.name}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-border/50 bg-background/60 p-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Email
                          </p>
                          <p className="mt-2 text-[14px] font-semibold text-foreground">
                            {activeUserDetail?.email_address ||
                              activeUserDetail?.email ||
                              'No email provided'}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                        <div className="space-y-1">
                          <Label>Assigned Roles</Label>
                          <p className="text-[12px] leading-5 text-muted-foreground">
                            This replaces the full role assignment list for the selected user.
                          </p>
                        </div>
                        <MultiSelect
                          options={roleOptions}
                          value={selectedUserRoleIds}
                          onChange={setSelectedUserRoleIds}
                          placeholder="Select roles"
                        />
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={() =>
                              saveUserRolesMutation.mutate({
                                userId: selectedUserId,
                                roleIds: selectedUserRoleIds,
                              })
                            }
                            disabled={saveUserRolesMutation.isPending || !selectedUserId}
                          >
                            <Save className="mr-2 h-4 w-4" />
                            {saveUserRolesMutation.isPending ? 'Saving roles...' : 'Save roles'}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                        <div className="space-y-1">
                          <Label>Direct Permissions</Label>
                          <p className="text-[12px] leading-5 text-muted-foreground">
                            Use direct permissions sparingly for exceptions that should not live in a role.
                          </p>
                        </div>
                        <MultiSelect
                          options={permissionOptions}
                          value={selectedUserPermissionIds}
                          onChange={setSelectedUserPermissionIds}
                          placeholder="Select direct permissions"
                        />
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={() =>
                              saveUserPermissionsMutation.mutate({
                                userId: selectedUserId,
                                permissionIds: selectedUserPermissionIds,
                              })
                            }
                            disabled={
                              saveUserPermissionsMutation.isPending || !selectedUserId
                            }
                          >
                            <Save className="mr-2 h-4 w-4" />
                            {saveUserPermissionsMutation.isPending
                              ? 'Saving permissions...'
                              : 'Save direct permissions'}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                        <div className="space-y-1">
                          <Label>Effective Permissions</Label>
                          <p className="text-[12px] leading-5 text-muted-foreground">
                            Permissions resolved from direct assignment and role membership.
                          </p>
                        </div>
                        <AssignmentBadgeList
                          items={
                            activeUserDetail?.authorization?.permission_names ||
                            activeUserDetail?.permission_names ||
                            []
                          }
                          getKey={(item) => item}
                          getLabel={(item) => item}
                          emptyLabel="No effective permissions were returned for this user."
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}

          {activeTab === 'roles' ? (
            <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
              <SidebarList
                title="Roles"
                description="Create new roles or refine existing permission bundles."
                countLabel={`${rolesQuery.data?.totalCount ?? 0} roles`}
                searchValue={roleSearch}
                onSearchChange={setRoleSearch}
                actions={
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setSelectedRoleId(NEW_ROLE_KEY)}
                  >
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    New role
                  </Button>
                }
                loading={rolesQuery.isLoading}
                items={rolesQuery.data?.roles ?? []}
                selectedId={selectedRoleId}
                onSelect={setSelectedRoleId}
                getItemId={getRoleId}
                renderItem={(item) => (
                  <>
                    <div className="flex w-full items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-[14px] font-semibold text-foreground">
                          {item?.name || 'Unnamed role'}
                        </div>
                        <div className="line-clamp-2 text-[12px] leading-5 text-muted-foreground">
                          {item?.description || 'No description provided.'}
                        </div>
                      </div>
                      <Badge
                        variant={item?.active === false ? 'outline' : 'secondary'}
                        className="rounded-full px-2.5 py-1"
                      >
                        {item?.active === false ? 'Inactive' : 'Active'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      {item?.assigned_users_count || item?.user_count ? (
                        <span>{`${item?.assigned_users_count || item?.user_count} users`}</span>
                      ) : null}
                    </div>
                  </>
                )}
                emptyTitle="No roles found"
                emptyDescription="Try a different search term or create the first role."
              />

              <Card>
                <CardHeader className="border-b border-border/50">
                  <CardTitle className="text-[18px]">
                    {selectedRoleId === NEW_ROLE_KEY ? 'Create Role' : 'Role Details'}
                  </CardTitle>
                  <CardDescription>
                    Update role metadata and the permissions granted by that role.
                  </CardDescription>
                  <label className="flex items-center gap-2 pt-3 text-[12px] text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={includeInactiveRoles}
                      onChange={(event) => setIncludeInactiveRoles(event.target.checked)}
                    />
                    Include inactive roles in the list
                  </label>
                </CardHeader>
                <CardContent className="space-y-6 px-5 py-5">
                  {roleDetailQuery.isLoading && selectedRoleId !== NEW_ROLE_KEY ? (
                    <div className="space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={`role-detail-loading-${index + 1}`}
                          className="h-20 animate-pulse rounded-2xl bg-muted/55"
                        />
                      ))}
                    </div>
                  ) : !selectedRoleId ? (
                    <EmptyState
                      icon={Shield}
                      title="Select a role"
                      description="Choose an existing role or create a new one to begin editing."
                    />
                  ) : (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="role-name">Role Name</Label>
                          <Input
                            id="role-name"
                            value={roleForm.name}
                            onChange={(event) =>
                              setRoleForm((current) => ({
                                ...current,
                                name: event.target.value,
                              }))
                            }
                            placeholder="Release Manager"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="block">Status</Label>
                          <label className="flex h-9 items-center gap-2 rounded-xl border border-input bg-background px-3 text-[13px]">
                            <input
                              type="checkbox"
                              checked={roleForm.active}
                              onChange={(event) =>
                                setRoleForm((current) => ({
                                  ...current,
                                  active: event.target.checked,
                                }))
                              }
                            />
                            Active role
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="role-description">Description</Label>
                        <Textarea
                          id="role-description"
                          value={roleForm.description}
                          onChange={(event) =>
                            setRoleForm((current) => ({
                              ...current,
                              description: event.target.value,
                            }))
                          }
                          placeholder="Describe the operational responsibilities this role should cover."
                        />
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-[12px] text-muted-foreground">
                          {selectedRoleId === NEW_ROLE_KEY
                            ? 'Save the role first to create it, then adjust assignments as needed.'
                            : 'Role changes are saved directly to the authorization service.'}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedRoleId && selectedRoleId !== NEW_ROLE_KEY ? (
                            <Button
                              type="button"
                              variant="destructive"
                              onClick={() => setConfirmRoleDeleteOpen(true)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete role
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            onClick={() => saveRoleMutation.mutate(roleForm)}
                            disabled={saveRoleMutation.isPending || !roleForm.name.trim()}
                          >
                            <Save className="mr-2 h-4 w-4" />
                            {saveRoleMutation.isPending ? 'Saving role...' : 'Save role'}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                        <div className="space-y-1">
                          <Label>Granted Permissions</Label>
                          <p className="text-[12px] leading-5 text-muted-foreground">
                            Replace the full permission set for this role.
                          </p>
                        </div>
                        <MultiSelect
                          options={permissionOptions}
                          value={selectedRolePermissionIds}
                          onChange={setSelectedRolePermissionIds}
                          placeholder="Select permissions for this role"
                        />
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={() =>
                              saveRolePermissionsMutation.mutate({
                                roleId: selectedRoleId,
                                permissionIds: selectedRolePermissionIds,
                              })
                            }
                            disabled={
                              saveRolePermissionsMutation.isPending ||
                              !selectedRoleId ||
                              selectedRoleId === NEW_ROLE_KEY
                            }
                          >
                            <Save className="mr-2 h-4 w-4" />
                            {saveRolePermissionsMutation.isPending
                              ? 'Saving permissions...'
                              : 'Save permissions'}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                        <div className="space-y-1">
                          <Label>Assigned Users</Label>
                          <p className="text-[12px] leading-5 text-muted-foreground">
                            Users currently inheriting access from this role.
                          </p>
                        </div>
                        <AssignmentBadgeList
                          items={
                            activeRoleDetail?.assigned_users ||
                            activeRoleDetail?.users ||
                            []
                          }
                          getKey={(item, index) => getUserId(item) || `role-user-${index}`}
                          getLabel={(item) =>
                            item?.username ||
                            item?.email_address ||
                            item?.email ||
                            'Unknown user'
                          }
                          emptyLabel="No assigned users were returned for this role."
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}

          {activeTab === 'permissions' ? (
            <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
              <SidebarList
                title="Permissions"
                description="Manage the permission catalog and its display metadata."
                countLabel={`${permissionsQuery.data?.totalCount ?? 0} permissions`}
                searchValue={permissionSearch}
                onSearchChange={setPermissionSearch}
                actions={
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setSelectedPermissionId(NEW_PERMISSION_KEY)}
                  >
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    New permission
                  </Button>
                }
                loading={permissionsQuery.isLoading}
                items={permissionsQuery.data?.permissions ?? []}
                selectedId={selectedPermissionId}
                onSelect={setSelectedPermissionId}
                getItemId={getPermissionId}
                renderItem={(item) => (
                  <>
                    <div className="flex w-full items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-[14px] font-semibold text-foreground">
                          {getPermissionLabel(item)}
                        </div>
                        <div className="truncate text-[12px] text-muted-foreground">
                          {item?.name || 'No permission key'}
                        </div>
                      </div>
                      <Badge
                        variant={item?.active === false ? 'outline' : 'secondary'}
                        className="rounded-full px-2.5 py-1"
                      >
                        {item?.active === false ? 'Inactive' : 'Active'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      {item?.module ? <span>{item.module}</span> : null}
                    </div>
                  </>
                )}
                emptyTitle="No permissions found"
                emptyDescription="Try a different search or create the first permission entry."
              />

              <Card>
                <CardHeader className="border-b border-border/50">
                  <CardTitle className="text-[18px]">
                    {selectedPermissionId === NEW_PERMISSION_KEY
                      ? 'Create Permission'
                      : 'Permission Details'}
                  </CardTitle>
                  <CardDescription>
                    Maintain permission keys, display labels, modules, and active state.
                  </CardDescription>
                  <div className="grid gap-3 pt-3 md:grid-cols-[minmax(0,1fr)_auto]">
                    <SearchField
                      value={permissionModuleFilter}
                      onChange={setPermissionModuleFilter}
                      placeholder="Filter by module..."
                    />
                    <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={includeInactivePermissions}
                        onChange={(event) =>
                          setIncludeInactivePermissions(event.target.checked)
                        }
                      />
                      Include inactive
                    </label>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 px-5 py-5">
                  {permissionDetailQuery.isLoading &&
                  selectedPermissionId !== NEW_PERMISSION_KEY ? (
                    <div className="space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={`permission-detail-loading-${index + 1}`}
                          className="h-20 animate-pulse rounded-2xl bg-muted/55"
                        />
                      ))}
                    </div>
                  ) : !selectedPermissionId ? (
                    <EmptyState
                      icon={KeyRound}
                      title="Select a permission"
                      description="Choose an existing permission or create a new one to begin editing."
                    />
                  ) : (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="permission-name">Permission Key</Label>
                          <Input
                            id="permission-name"
                            value={permissionForm.name}
                            onChange={(event) =>
                              setPermissionForm((current) => ({
                                ...current,
                                name: event.target.value,
                              }))
                            }
                            placeholder="rollouts.approve"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="permission-display-name">Display Name</Label>
                          <Input
                            id="permission-display-name"
                            value={permissionForm.display_name}
                            onChange={(event) =>
                              setPermissionForm((current) => ({
                                ...current,
                                display_name: event.target.value,
                              }))
                            }
                            placeholder="Approve Rollouts"
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="permission-module">Module</Label>
                          <Input
                            id="permission-module"
                            value={permissionForm.module}
                            list="permission-module-options"
                            onChange={(event) =>
                              setPermissionForm((current) => ({
                                ...current,
                                module: event.target.value,
                              }))
                            }
                            placeholder="rollouts"
                          />
                          <datalist id="permission-module-options">
                            {availableModules.map((moduleName) => (
                              <option key={moduleName} value={moduleName} />
                            ))}
                          </datalist>
                        </div>
                        <div className="space-y-2">
                          <Label className="block">Status</Label>
                          <label className="flex h-9 items-center gap-2 rounded-xl border border-input bg-background px-3 text-[13px]">
                            <input
                              type="checkbox"
                              checked={permissionForm.active}
                              onChange={(event) =>
                                setPermissionForm((current) => ({
                                  ...current,
                                  active: event.target.checked,
                                }))
                              }
                            />
                            Active permission
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="permission-description">Description</Label>
                        <Textarea
                          id="permission-description"
                          value={permissionForm.description}
                          onChange={(event) =>
                            setPermissionForm((current) => ({
                              ...current,
                              description: event.target.value,
                            }))
                          }
                          placeholder="Explain what this permission grants in the rollout workspace."
                        />
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-[12px] text-muted-foreground">
                          Keep permission keys stable after rollout so frontend and backend checks stay aligned.
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedPermissionId &&
                          selectedPermissionId !== NEW_PERMISSION_KEY ? (
                            <Button
                              type="button"
                              variant="destructive"
                              onClick={() => setConfirmPermissionDeleteOpen(true)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete permission
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            onClick={() => savePermissionMutation.mutate(permissionForm)}
                            disabled={
                              savePermissionMutation.isPending ||
                              !permissionForm.name.trim() ||
                              !permissionForm.display_name.trim()
                            }
                          >
                            <Save className="mr-2 h-4 w-4" />
                            {savePermissionMutation.isPending
                              ? 'Saving permission...'
                              : 'Save permission'}
                          </Button>
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                          <div className="space-y-1">
                            <Label>Assigned Roles</Label>
                            <p className="text-[12px] leading-5 text-muted-foreground">
                              Roles currently inheriting this permission.
                            </p>
                          </div>
                          <AssignmentBadgeList
                            items={permissionAssignments.assignedRoles}
                            getKey={(item, index) =>
                              getRoleId(item) ||
                              `${typeof item === 'string' ? item : item?.name || 'role'}-${index}`
                            }
                            getLabel={(item) =>
                              typeof item === 'string'
                                ? item
                                : item?.name || item?.display_name || 'Role'
                            }
                            emptyLabel="No role assignments were returned for this permission."
                          />
                        </div>

                        <div className="space-y-3 rounded-2xl border border-border/50 bg-background/60 p-4">
                          <div className="space-y-1">
                            <Label>Direct User Assignments</Label>
                            <p className="text-[12px] leading-5 text-muted-foreground">
                              Users with this permission assigned directly.
                            </p>
                          </div>
                          <AssignmentBadgeList
                            items={permissionAssignments.assignedUsers}
                            getKey={(item, index) =>
                              getUserId(item) || `${item?.username || 'user'}-${index}`
                            }
                            getLabel={(item) =>
                              item?.username ||
                              item?.email_address ||
                              item?.email ||
                              'User'
                            }
                            emptyLabel="No direct user assignments were returned for this permission."
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
        </AppPageSection>
      </div>

      <Dialog open={confirmRoleDeleteOpen} onOpenChange={setConfirmRoleDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete role</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] leading-6 text-muted-foreground">
            This will soft-delete{' '}
            <span className="font-semibold text-foreground">
              {roleForm.name || 'the selected role'}
            </span>
            . Existing assignments may still be affected by backend validation rules.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRoleDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteRoleMutation.mutate()}
              disabled={deleteRoleMutation.isPending}
            >
              {deleteRoleMutation.isPending ? 'Deleting...' : 'Delete role'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmPermissionDeleteOpen}
        onOpenChange={setConfirmPermissionDeleteOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete permission</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] leading-6 text-muted-foreground">
            This will soft-delete{' '}
            <span className="font-semibold text-foreground">
              {permissionForm.display_name || permissionForm.name || 'the selected permission'}
            </span>
            . Make sure downstream role assignments are updated as needed.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmPermissionDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deletePermissionMutation.mutate()}
              disabled={deletePermissionMutation.isPending}
            >
              {deletePermissionMutation.isPending
                ? 'Deleting...'
                : 'Delete permission'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={feedbackDialog.open}
        onOpenChange={(open) =>
          setFeedbackDialog((current) => ({
            ...current,
            open,
          }))
        }
      >
        <DialogContent
          className={cn(
            'max-w-lg gap-0 overflow-hidden rounded-2xl bg-background p-0 shadow-2xl',
            feedbackDialog.tone === 'success'
              ? 'border border-emerald-500/20'
              : 'border border-rose-500/20'
          )}
        >
          <DialogHeader
            className={cn(
              'border-b border-border/60 px-5 py-4',
              feedbackDialog.tone === 'success'
                ? 'bg-emerald-500/6'
                : 'bg-rose-500/6'
            )}
          >
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                  feedbackDialog.tone === 'success'
                    ? 'bg-emerald-500/12 text-emerald-600 dark:text-emerald-300'
                    : 'bg-rose-500/12 text-rose-600 dark:text-rose-300'
                )}
              >
                {feedbackDialog.tone === 'success' ? (
                  <CheckCircle2 className="h-[18px] w-[18px]" />
                ) : (
                  <AlertTriangle className="h-[18px] w-[18px]" />
                )}
              </span>
              <div className="space-y-1">
                <DialogTitle className="text-base font-semibold text-foreground">
                  {feedbackDialog.title ||
                    (feedbackDialog.tone === 'success'
                      ? 'Success'
                      : 'Something went wrong')}
                </DialogTitle>
                <p className="text-[13px] leading-5 text-muted-foreground">
                  {feedbackDialog.tone === 'success'
                    ? 'Your authorization changes were saved successfully.'
                    : 'The authorization update could not be completed. Review the details below and try again.'}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 px-5 py-5">
            <div
              className={cn(
                'rounded-xl px-4 py-3',
                feedbackDialog.tone === 'success'
                  ? 'border border-emerald-500/15 bg-emerald-500/6'
                  : 'border border-rose-500/15 bg-rose-500/6'
              )}
            >
              <p className="whitespace-pre-wrap break-words text-[13px] leading-6 text-foreground">
                {feedbackDialog.message ||
                  (feedbackDialog.tone === 'success'
                    ? 'The action completed successfully.'
                    : 'An unexpected error occurred.')}
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={closeFeedbackDialog}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Authorization;
